import React, { forwardRef } from 'react';
import { cx } from "@/utils/cx";
import { Icons } from "@/assets";

interface MultiSelectCheckboxGroupProps {
    label: string;
    options: { label: string; value: string }[];
    value?: string;
    onChange?: (val: string) => void;
    error?: string;
    disabled?: boolean;
    required?: boolean;
    singleSelect?: boolean;
}

export const MultiSelectCheckboxGroup = forwardRef<HTMLDivElement, MultiSelectCheckboxGroupProps>(({ 
    label, options, value = "", onChange, error, disabled, required, singleSelect = false
}, ref) => {
    
    const selectedValue = Array.isArray(value) ? value.join(",") : value || "";
    const normalizeToken = (token: string) => String(token || "").trim();
    const selectedValues = selectedValue
        .split(',')
        .map((item) => normalizeToken(item))
        .filter(Boolean);

    const handleToggle = (optValue: string) => {
        if (disabled) return;

        const normalizedOptValue = normalizeToken(optValue);

        if (singleSelect) {
            onChange?.(selectedValues.includes(normalizedOptValue) ? "" : optValue);
            return;
        }

        const nextValues = selectedValues.includes(normalizedOptValue)
            ? selectedValues.filter((item) => item !== normalizedOptValue)
            : [...selectedValues, optValue];

        onChange?.(nextValues.join(','));
    };

    return (
        <div ref={ref} className="flex flex-col border-b border-gray-200 last:border-0 w-full gap-3">
            <div className="flex items-center ">
                <h4 className="font-medium text-gray-900">{label} {required && <span className="text-error ml-1">*</span>}</h4>
            </div>
            
            <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-x-6 gap-y-4">
                {options.map((opt) => {
                    const normalizedOptionValue = normalizeToken(opt.value);
                    const isChecked = singleSelect
                        ? selectedValues.includes(normalizedOptionValue)
                        : selectedValues.includes(normalizedOptionValue);
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="checkbox"
                            aria-checked={isChecked}
                            aria-disabled={disabled}
                            onClick={() => handleToggle(opt.value)}
                            className={cx(
                            "group flex min-w-0 items-start gap-3 select-none text-left",
                            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                        )}
                        >
                            <span
                                className={cx(
                                    "w-4 h-4 rounded-[4px] border-[1.6px] flex items-center justify-center transition-colors shrink-0",
                                    isChecked
                                        ? "bg-[#0a68ff] border-[#0a68ff]"
                                        : "bg-white border-gray-30 group-hover:border-gray-400",
                                    error && !isChecked && "border-[#ff424f]"
                                )}
                            >
                                {isChecked && <Icons.TickIcon className="text-white w-3 h-3" />}
                            </span>
                            <span className="min-w-0 whitespace-normal break-words text-sm font-normal leading-6 text-gray-100">{opt.label}</span>
                        </button>
                    );
                })}
            </div>
            {error && <p className="text-xs text-[#ff424f] mt-2">{error}</p>}
        </div>
    );
});

MultiSelectCheckboxGroup.displayName = "MultiSelectCheckboxGroup";
